import { useEffect, useRef, type ReactNode } from 'react';
import { Box, Button, Grid, HStack, Stack, Text, chakra } from '@chakra-ui/react';
import type { Screen } from '@casa/model';
import { IconGlyph } from '../ui/icon_glyph.tsx';
import { MENU_BUTTON_COLOR, MUTED_COLOR } from '../ui/theme.ts';
import { useElementSize } from '../ui/use_element_size.ts';

/**
 * Where the menu is: a column beside the screens (tablets in landscape, computers), a bar below
 * them (tablets in portrait, LK-10), or the phone's drawer (LK-6).
 */
export type MenuLayout = 'column' | 'bar' | 'drawer';

export interface SideMenuProps {
    screens: readonly Screen[];
    /** The screen shown, marked in the menu (look LK-1). */
    current: number;
    onSelectScreen: (index: number) => void;
    layout: MenuLayout;
    isEditing: boolean;
    hasChanges: boolean;
    /** The settings; on a phone the drawer closes first (Mac check of E3b, note 1). */
    onSettings: () => void;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    /** In edit mode, in place of the settings: add a tile, the screens. */
    onAddTile: () => void;
    onScreens: () => void;
    /** In edit mode: the dashboard's connections. */
    onConnections: () => void;
    /** The dashboard's default tile colours (T-2a). */
    onColors: () => void;
}

/** The column's width on every device (owner's testing note, misc.md 3): room for the names (LK-3). */
export const MENU_COLUMN_WIDTH = 92;
/**
 * The space kept clear of a rounded screen corner (an iPad, Mac check of E1): half the bottom inset,
 * on the right and at the bottom. The column is this much wider, so that its contents keep their
 * 92 px and the names their room (Mac check of E3d, note 1).
 */
export const CORNER_CLEARANCE = 'max(0px, calc(env(safe-area-inset-bottom) / 2))';
/** The bar's height above the safe area: a square, its name, and the space around them. */
export const MENU_BAR_HEIGHT = 80;
/** A screen's square in the bar, and the width it takes with its name. */
const BAR_SQUARE = 44;
const BAR_ITEM_WIDTH = 72;
/** Below this width the column has no names, and the app's buttons stack (a safe area can narrow it). */
const ROOMY_FROM_WIDTH = 88;
/** The space around the column's contents. */
const COLUMN_PADDING = 8;
/** More screens than fit scroll; a thin scrollbar where the system draws one (Windows), not a thick one over the names. */
const SCROLLING = { scrollbarWidth: 'thin', scrollbarColor: '#505050 transparent', overscrollBehavior: 'none' } as const;

const PlainButton = chakra('button', {
    base: {
        cursor: 'pointer',
        bg: 'transparent',
        border: 'none',
        color: 'inherit',
        _focusVisible: { outline: '2px solid', outlineColor: 'casa.accent', outlineOffset: '2px' },
    },
});

/**
 * The menu (look LK-1 to LK-6, LK-10): the screens as rounded squares, the current one in the
 * accent colour; the app's buttons in a group of their own, as bare icons, so that a screen's icon
 * is never taken for one of them. In edit mode the app's group takes the accent colour, with + and
 * the screens, and Cancel and Save labelled.
 */
export function SideMenu(props: SideMenuProps) : ReactNode {
    const { screens, current, onSelectScreen, layout } = props;
    const [rootRef, { width }] = useElementSize<HTMLDivElement>();

    const buttons = screens.map((screen, index) => ({
        key: index,
        screen,
        index,
        isCurrent: index === current,
        onSelect: () => onSelectScreen(index),
    }));

    if (layout === 'bar') {
        return (
            <HStack className="chrome" boxSizing="border-box" bg="casa.panel" height="100%" gap="0" borderTop="1px solid" borderColor="casa.line" pb="env(safe-area-inset-bottom)" px="2">
                <HStack as="nav" aria-label="Screens" gap="1" flex="1" minW="0" height="100%" overflowX="auto" css={SCROLLING}>
                    {buttons.map((button) => (
                        <ScreenButton {...button} key={button.key} isRow={false} square={BAR_SQUARE} itemWidth={BAR_ITEM_WIDTH} showsName />
                    ))}
                </HStack>
                <AppButtons {...props} />
            </HStack>
        );
    }

    const isDrawer = layout === 'drawer';
    const square = Math.max(0, width - 2 * COLUMN_PADDING);
    const isRoomy = width >= ROOMY_FROM_WIDTH;

    return (
        <Stack
            ref={rootRef}
            className="chrome"
            // The reset (border-box) applies inside `.chrome`, not to it; the padding is in the height.
            boxSizing="border-box"
            bg="casa.panel"
            height="100%"
            minW="0"
            overflowX="hidden"
            gap="0"
            justifyContent="space-between"
            pr={CORNER_CLEARANCE}
            pb={CORNER_CLEARANCE}
        >
            <Stack as="nav" aria-label="Screens" gap={isDrawer ? '1' : '3'} p={`${COLUMN_PADDING}px`} overflowY="auto" minH="0" css={SCROLLING}>
                {buttons.map((button) => (
                    <ScreenButton {...button} key={button.key} isRow={isDrawer} square={square} showsName={isDrawer || isRoomy} />
                ))}
            </Stack>
            <AppButtons {...props} isRoomy={isRoomy} />
        </Stack>
    );
}

interface ScreenButtonProps {
    screen: Screen;
    index: number;
    isCurrent: boolean;
    isRow: boolean;
    /** The square's side. */
    square: number;
    /** The width the square and its name take; the square's otherwise. */
    itemWidth?: number;
    showsName: boolean;
    onSelect: () => void;
}

function ScreenButton({ screen, index, isCurrent, isRow, square, itemWidth, showsName, onSelect }: ScreenButtonProps) : ReactNode {
    const name = screen.name || `Screen ${index + 1}`;
    const iconColor = isCurrent ? MENU_BUTTON_COLOR : MUTED_COLOR;
    const ref = useRef<HTMLButtonElement>(null);

    // The current screen stays in view when there are more than fit, whichever way it was chosen.
    useEffect(() => {
        if (isCurrent) {
            ref.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
    }, [isCurrent]);

    if (isRow) {
        return (
            <PlainButton
                ref={ref}
                aria-current={isCurrent ? 'page' : undefined}
                display="flex"
                alignItems="center"
                gap="3"
                p="1.5"
                borderRadius="casa"
                textAlign="left"
                bg={isCurrent ? 'casa.accent' : undefined}
                onClick={onSelect}
            >
                <Box display="flex" alignItems="center" justifyContent="center" w="40px" h="40px" flex="none" borderRadius="casa" bg={isCurrent ? undefined : 'casa.raised'}>
                    <IconGlyph name={screen.icon ?? 'square'} size={22} color={iconColor} />
                </Box>
                <Text truncate fontSize="md" color={isCurrent ? 'white' : 'casa.muted'}>
                    {name}
                </Text>
            </PlainButton>
        );
    }

    return (
        <PlainButton
            ref={ref}
            aria-label={name}
            aria-current={isCurrent ? 'page' : undefined}
            title={name}
            display="flex"
            flexDirection="column"
            alignItems="center"
            flex="none"
            w={itemWidth === undefined ? undefined : `${itemWidth}px`}
            gap="1"
            p="0"
            onClick={onSelect}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                w={`${square}px`}
                h={`${square}px`}
                borderRadius="casa"
                bg={isCurrent ? 'casa.accent' : 'casa.raised'}
            >
                <IconGlyph name={screen.icon ?? 'square'} size={Math.round(square * 0.5)} color={iconColor} />
            </Box>
            {showsName && (
                <Text aria-hidden w="100%" fontSize="xs" lineHeight="short" lineClamp={itemWidth === undefined ? 2 : 1} textAlign="center" wordBreak="break-word" color={isCurrent ? 'white' : 'casa.muted'}>
                    {name}
                </Text>
            )}
        </PlainButton>
    );
}

/** The app's buttons, in a group of their own (LK-2), the accent colour in edit mode (LK-4). */
function AppButtons({ layout, isRoomy = true, isEditing, hasChanges, onSettings, onEdit, onCancel, onSave, onAddTile, onScreens, onConnections, onColors }: SideMenuProps & { isRoomy?: boolean }) : ReactNode {
    // Reload is in the settings (DB-8; the owner on #26: "a little too prominent" in the menu).
    const tools: { icon: string; label: string; onClick: () => void }[] = isEditing
        ? [
            { icon: 'plus', label: 'Add a tile', onClick: onAddTile },
            { icon: 'rectangle-history', label: 'Screens', onClick: onScreens },
            { icon: 'plug', label: 'Connections', onClick: onConnections },
            { icon: 'palette', label: 'Dashboard style', onClick: onColors },
        ]
        : [
            { icon: 'gear', label: 'Settings', onClick: onSettings },
            { icon: 'edit', label: 'Edit', onClick: onEdit },
        ];
    const iconButtons = tools.map((tool) => (
        <PlainButton key={tool.icon} aria-label={tool.label} title={tool.label} display="flex" p="1.5" borderRadius="md" onClick={tool.onClick}>
            <IconGlyph name={tool.icon} size={22} color={MENU_BUTTON_COLOR} />
        </PlainButton>
    ));
    const cancelAndSave = isEditing
        ? [
            <Button key="cancel" size="xs" variant="outline" color="white" borderColor="casa.line" px={layout === 'bar' ? '3' : '1'} onClick={onCancel}>
                Cancel
            </Button>,
            <Button
                key="save"
                size="xs"
                bg="casa.accent"
                color="white"
                px={layout === 'bar' ? '3' : '1'}
                position="relative"
                aria-label={hasChanges ? 'Save, with unsaved changes' : 'Save'}
                onClick={onSave}
            >
                Save
                {hasChanges && (
                    <Box position="absolute" top="-4px" right="-4px" w="12px" h="12px" borderRadius="full" bg="casa.unsaved" borderWidth="2px" borderColor="casa.panel" />
                )}
            </Button>,
        ]
        : [];
    const groupStyle = isEditing ? { bg: 'casa.accentTint', borderColor: 'casa.accent' } : { borderColor: 'casa.line' };

    if (layout === 'bar') {
        return (
            <HStack
                gap="2"
                flex="none"
                alignSelf="stretch"
                my="2"
                ps="3"
                pe={isEditing ? '2' : '0'}
                {...(isEditing ? { border: '1px solid', borderRadius: 'casa' } : { borderLeft: '1px solid' })}
                {...groupStyle}
            >
                {iconButtons}
                {cancelAndSave}
            </HStack>
        );
    }

    return (
        <Stack gap="2" p="2" borderTop="1px solid" {...groupStyle}>
            {layout === 'drawer' ? (
                <Stack gap="0">
                    {tools.map((tool) => (
                        <PlainButton key={tool.icon} display="flex" alignItems="center" gap="3" px="2" py="2" borderRadius="casa" textAlign="left" onClick={tool.onClick}>
                            <IconGlyph name={tool.icon} size={22} color={MENU_BUTTON_COLOR} />
                            <Text fontSize="md">{tool.label}</Text>
                        </PlainButton>
                    ))}
                </Stack>
            ) : (
                <Grid templateColumns={isRoomy ? '1fr 1fr' : '1fr'} gap="1" justifyItems="center">
                    {iconButtons}
                </Grid>
            )}
            {isEditing && <Stack gap="1.5">{cancelAndSave}</Stack>}
        </Stack>
    );
}
